import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Maximize, ArrowRight, Shield, Building2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { getThumbnailUrl } from '../services/api';

function formatInrDisplay(amount) {
  if (!amount || amount === 0) return '';
  if (amount >= 10000000) {
    const cr = amount / 10000000;
    return `₹${cr % 1 === 0 ? cr : cr.toFixed(1)} Cr`;
  }
  if (amount >= 100000) {
    const lac = amount / 100000;
    return `₹${lac % 1 === 0 ? lac : lac.toFixed(1)} Lac`;
  }
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatUsdDisplay(amount) {
  if (!amount || amount === 0) return '';
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount.toLocaleString()}`;
}

export default function PropertyCard({ property }) {
  const isIndia = property.country === 'India' || property.market === 'india';
  const isProject = property.listing_type === 'project_villa' || property.listing_type === 'project_plot';

  const displayPrice = isIndia
    ? (property.price_inr ? formatInrDisplay(property.price_inr) : (property.price_range_display || formatInrDisplay(property.price_usd * 83.5)))
    : formatUsdDisplay(property.price_usd);

  const locationText = property.micromarket
    ? `${property.micromarket}, ${property.city}`
    : `${property.city}, ${property.country}`;

  return (
    <Link 
      to={`/properties/${property.id}`}
      className="group block property-card bg-[#1A1D24] border border-[#2A2F3A] rounded-xl overflow-hidden hover:border-[#D4AF37]/50"
      data-testid={`property-card-${property.id}`}
    >
      <div className="relative h-64 image-zoom">
        <img src={getThumbnailUrl(property.image, 600)} alt={property.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        <div className="absolute top-4 left-4 flex flex-wrap gap-2">
          {property.visa_eligible && property.country !== 'India' && (
            <Badge className="bg-[#D4AF37] text-[#0B0C10] hover:bg-[#D4AF37]">
              <Shield className="h-3 w-3 mr-1" />{property.visa_type}
            </Badge>
          )}
          {isProject && (
            <Badge className="bg-emerald-500/90 text-white hover:bg-emerald-500">
              <Building2 className="h-3 w-3 mr-1" />{property.listing_type === 'project_villa' ? 'Villa Project' : 'Plotted Project'}
            </Badge>
          )}
          <Badge className="bg-[#1A1D24]/80 text-white backdrop-blur-sm border border-white/20">
            {property.property_type}
          </Badge>
          {property.rera_number && (
            <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30">RERA</Badge>
          )}
        </div>
        
        <div className="absolute bottom-4 left-4">
          <div className="text-white/70 text-xs uppercase tracking-wider mb-1">{isProject ? 'Starting' : 'From'}</div>
          <div className="text-2xl font-semibold text-white heading-display">{displayPrice}</div>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl text-white font-medium mb-2 group-hover:text-[#D4AF37] transition-colors line-clamp-1">
          {property.title}
        </h3>
        
        <div className="flex items-center text-[#A1A1AA] text-sm mb-4">
          <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{locationText}</span>
        </div>

        <div className="flex items-center justify-between text-[#A1A1AA] text-sm border-t border-[#2A2F3A] pt-4">
          <div className="flex items-center space-x-4">
            {isProject && property.configurations?.length > 0 ? (
              <span className="text-xs">{property.configurations.join(', ')}</span>
            ) : (
              <>
                {property.bedrooms > 0 && <span className="flex items-center"><Bed className="h-4 w-4 mr-1" />{property.bedrooms}</span>}
                {property.bathrooms > 0 && <span className="flex items-center"><Bath className="h-4 w-4 mr-1" />{property.bathrooms}</span>}
              </>
            )}
            {property.area_sqft > 0 && (
              <span className="flex items-center"><Maximize className="h-4 w-4 mr-1" />{property.area_sqft.toLocaleString()} sqft</span>
            )}
          </div>
          <span className="text-[#D4AF37] group-hover:translate-x-1 transition-transform">
            <ArrowRight className="h-5 w-5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
